/**
 * Une photo réglable depuis l'admin, affichée sans clignotement.
 *
 * ## Le cadre existe avant la photo
 *
 * Le conteneur porte la taille — c'est l'appelant qui la donne, par exemple
 * `aspect-[4/3] w-full`. Il est donc dessiné dès le premier rendu, que l'on
 * connaisse l'adresse de l'image ou non, et il ne bouge plus. Sans lui, un
 * `<img>` sans dimensions occupe zéro pixel de haut jusqu'à ce que l'image
 * arrive, et toute la page saute quand elle se pose.
 *
 * ## Ce qu'on affiche quand on ne sait pas encore
 *
 * Un dégradé aux couleurs du site, rien d'autre. Pas d'ancienne photo — c'est
 * précisément ce qu'on cherche à ne plus montrer. Pas de pastille tournante
 * non plus : elle durerait moins longtemps que le temps de la remarquer, et
 * clignoterait pour rien.
 *
 * ## Le recadrage est le même qu'à l'admin
 *
 * `object-cover` dans un cadre au rapport fixe : c'est exactement l'aperçu que
 * voit la personne qui téléverse. Ce qu'elle valide est ce qui s'affiche.
 */

import type { Photo } from '../../lib/reglages';

const PhotoReglage = ({
  photo,
  className = '',
}: {
  /** `null` ou `undefined` : l'adresse n'est pas encore connue. */
  photo?: Photo | null;
  /** La taille et la forme du cadre — `aspect-[4/3] w-full rounded-xl`, etc. */
  className?: string;
}) => (
  <div
    className={`overflow-hidden bg-gradient-to-br from-primary-50 to-blue-100 ${className}`.trim()}
  >
    {photo && (
      <img
        src={photo.url}
        alt={photo.alt}
        className="h-full w-full object-cover"
        decoding="async"
      />
    )}
  </div>
);

export default PhotoReglage;
